import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod/v3";
import { useForgotPassword } from "@/api/auth/forgot-password";
import { AuthDescription, AuthLinkButton, AuthSplitLayout, AuthTitle } from "@/components/auth/auth-split-layout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";

const formSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Enter the email you sign in with.")
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "That doesn't look like a full email address — check it and try again."),
});

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof formSchema>>({ resolver: zodResolver(formSchema), defaultValues: { email: "" } });
  const { mutate, isPending } = useForgotPassword();
  const [sentTo, setSentTo] = useState<string | null>(null);

  const handleSubmit = ({ email }: z.infer<typeof formSchema>) => {
    mutate({ email }, { onSuccess: () => setSentTo(email) });
  };

  const backToSignIn = () => navigate({ to: "/login" });

  if (sentTo) {
    return (
      <AuthSplitLayout>
        <div className="flex w-full max-w-[350px] flex-col" data-testid="forgot-password-success-page">
          <AuthTitle>Check your inbox</AuthTitle>
          <AuthDescription>
            We've sent a reset link to <span className="font-semibold text-gray-900">{sentTo}</span>. Open it to choose
            a new password, then sign in with it.
          </AuthDescription>
          <Button size="lg" className="mt-7 w-full" onClick={backToSignIn}>
            Back to log in
          </Button>
          <AuthLinkButton className="mt-5" onClick={() => setSentTo(null)}>
            Use a different email
          </AuthLinkButton>
        </div>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout>
      <form
        className="flex w-full max-w-[350px] flex-col"
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
        data-testid="forgot-password-page"
      >
        <AuthTitle>Reset your password</AuthTitle>
        <AuthDescription>
          Enter the email you sign in with and we'll send you a link to choose a new password.
        </AuthDescription>
        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <Input
              className="mt-7"
              label="Email"
              type="email"
              autoFocus
              placeholder="example@email.com"
              autoComplete="username"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              isInvalid={!!fieldState.error}
              hint={fieldState.error?.message}
            />
          )}
        />
        <Button type="submit" size="lg" className="mt-6 w-full" isLoading={isPending}>
          Send reset link
        </Button>
        <AuthLinkButton className="mt-5" onClick={backToSignIn}>
          Back to log in
        </AuthLinkButton>
      </form>
    </AuthSplitLayout>
  );
};
